import api from "@/assets/Js/api.js";

export default {
  name: "SystemDashboard",

  data() {
    return {
      isLoading: false,
      errorMessage: "",
      salesPeriod: "week",
      currentDate: new Date().toLocaleDateString("zh-TW", {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long",
      }),
      today: {
        totalOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        completedRevenue: 0,
        avgOrderValue: 0,
        newCustomers: 0,
      },
      yesterday: {
        totalOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        completedRevenue: 0,
        avgOrderValue: 0,
        newCustomers: 0,
      },
      rangeStart: null,
      rangeEnd: null,
      salesTrend: [],
      hotProducts: [],
      recentOrders: [],
      paymentMethods: [],
    };
  },

  computed: {
    statsCards() {
      return [
        {
          key: "orders",
          icon: "fas fa-shopping-cart",
          label: "今日訂單總數",
          value: this.formatNumber(this.today.totalOrders),
          trend: this.calculateGrowth(
            this.today.totalOrders,
            this.yesterday.totalOrders
          ),
          inverseTrend: false,
        },
        {
          key: "revenue",
          icon: "fas fa-dollar-sign",
          label: "今日已完成營業額",
          value: this.formatCurrency(this.today.completedRevenue),
          trend: this.calculateGrowth(
            this.today.completedRevenue,
            this.yesterday.completedRevenue
          ),
          inverseTrend: false,
        },
        {
          key: "members",
          icon: "fas fa-users",
          label: "今日新增會員",
          value: this.formatNumber(this.today.newCustomers),
          trend: this.calculateGrowth(
            this.today.newCustomers,
            this.yesterday.newCustomers
          ),
          inverseTrend: false,
        },
        {
          key: "cancelled",
          icon: "fas fa-ban",
          label: "今日取消訂單",
          value: this.formatNumber(this.today.cancelledOrders),
          trend: this.calculateGrowth(
            this.today.cancelledOrders,
            this.yesterday.cancelledOrders
          ),
          inverseTrend: true,
        },
      ];
    },

    normalizedSalesBars() {
      const rows = this.buildCompleteTrend();
      const maxRevenue = Math.max(
        0,
        ...rows.map((item) => Number(item.revenue || 0))
      );

      return rows.map((item) => ({
        ...item,
        height:
          maxRevenue <= 0
            ? 0
            : Math.max(4, Math.round((item.revenue / maxRevenue) * 100)),
      }));
    },

    maxHotProductQuantity() {
      return Math.max(
        0,
        ...this.hotProducts.map((item) => Number(item.totalQuantity || 0))
      );
    },

    paymentPieStyle() {
      if (this.paymentMethods.length === 0) {
        return { background: "#eee9df" };
      }

      const colors = ["#b8592f", "#2f6f9c", "#1f8a52", "#b8860f", "#7b5ea7"];
      let current = 0;
      const segments = this.paymentMethods.map((item, index) => {
        const start = current;
        current += Number(item.percentage || 0);
        return `${colors[index % colors.length]} ${start}% ${current}%`;
      });

      return { background: `conic-gradient(${segments.join(", ")})` };
    },
  },

  watch: {
    salesPeriod() {
      this.loadDashboard();
    },
  },

  methods: {
    createEmptySummary() {
      return {
        totalOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        completedRevenue: 0,
        avgOrderValue: 0,
        newCustomers: 0,
      };
    },

    async loadDashboard() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.errorMessage = "";
      try {
        const response = await api.get("/report/dashboard", {
          period: this.salesPeriod,
        });
        const data = response.data?.data;
        if (!response.data?.success || !data) {
          throw new Error(response.data?.message || "儀錶板資料格式錯誤");
        }

        this.today = this.normalizeSummary(data.today ?? data.Today);
        this.yesterday = this.normalizeSummary(
          data.yesterday ?? data.Yesterday
        );
        this.rangeStart = data.rangeStart ?? data.RangeStart ?? null;
        this.rangeEnd = data.rangeEnd ?? data.RangeEnd ?? null;
        this.salesTrend = this.normalizeSalesTrend(
          data.salesTrend ?? data.SalesTrend
        );
        this.hotProducts = this.normalizeHotProducts(
          data.hotProducts ?? data.HotProducts
        );
        this.recentOrders = this.normalizeRecentOrders(
          data.recentOrders ?? data.RecentOrders
        );
        this.paymentMethods = this.normalizePaymentMethods(
          data.paymentMethods ?? data.PaymentMethods
        );
      } catch (error) {
        console.error("載入儀錶板真實資料失敗：", error);
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          "無法取得儀錶板資料";
        this.today = this.createEmptySummary();
        this.yesterday = this.createEmptySummary();
        this.salesTrend = [];
        this.hotProducts = [];
        this.recentOrders = [];
        this.paymentMethods = [];
      } finally {
        this.isLoading = false;
      }
    },

    normalizeSummary(value = {}) {
      return {
        totalOrders: Number(value.totalOrders ?? value.TotalOrders ?? 0),
        completedOrders: Number(
          value.completedOrders ?? value.CompletedOrders ?? 0
        ),
        cancelledOrders: Number(
          value.cancelledOrders ?? value.CancelledOrders ?? 0
        ),
        completedRevenue: Number(
          value.completedRevenue ?? value.CompletedRevenue ?? 0
        ),
        avgOrderValue: Number(
          value.avgOrderValue ?? value.AvgOrderValue ?? 0
        ),
        newCustomers: Number(
          value.newCustomers ?? value.NewCustomers ?? 0
        ),
      };
    },

    normalizeSalesTrend(items) {
      return (Array.isArray(items) ? items : []).map((item) => ({
        date: String(item.date ?? item.Date ?? "").split("T")[0],
        revenue: Number(item.revenue ?? item.Revenue ?? 0),
        orders: Number(item.orders ?? item.Orders ?? 0),
      }));
    },

    normalizeHotProducts(items) {
      return (Array.isArray(items) ? items : []).map((item) => ({
        menuItemId: Number(item.menuItemId ?? item.MenuItemId ?? 0),
        name: item.name ?? item.Name ?? "未命名商品",
        categoryName:
          item.categoryName ?? item.CategoryName ?? "未分類",
        totalQuantity: Number(
          item.totalQuantity ?? item.TotalQuantity ?? 0
        ),
        totalSales: Number(item.totalSales ?? item.TotalSales ?? 0),
      }));
    },

    normalizeRecentOrders(items) {
      return (Array.isArray(items) ? items : []).map((item) => ({
        id: Number(item.id ?? item.Id ?? 0),
        orderNumber:
          item.orderNumber ?? item.OrderNumber ?? `ORD-${item.id ?? item.Id}`,
        customerName:
          item.customerName ?? item.CustomerName ?? "散客",
        totalAmount: Number(item.totalAmount ?? item.TotalAmount ?? 0),
        status: item.status ?? item.Status ?? "未知",
        orderType: item.orderType ?? item.OrderType ?? "",
        orderDate: item.orderDate ?? item.OrderDate ?? null,
      }));
    },

    normalizePaymentMethods(items) {
      return (Array.isArray(items) ? items : []).map((item) => ({
        paymentMethod:
          item.paymentMethod ?? item.PaymentMethod ?? "其他",
        amount: Number(item.amount ?? item.Amount ?? 0),
        percentage: Number(item.percentage ?? item.Percentage ?? 0),
      }));
    },

    calculateGrowth(currentValue, previousValue) {
      const current = Number(currentValue || 0);
      const previous = Number(previousValue || 0);
      if (previous === 0) return current > 0 ? 100 : 0;
      return Math.round(((current - previous) / previous) * 100);
    },

    isTrendPositive(card) {
      return card.inverseTrend ? card.trend <= 0 : card.trend >= 0;
    },

    buildCompleteTrend() {
      if (!this.rangeStart || !this.rangeEnd) return [];

      const sourceMap = new Map(
        this.salesTrend.map((item) => [item.date, item])
      );
      const start = new Date(`${String(this.rangeStart).split("T")[0]}T00:00:00`);
      const end = new Date(`${String(this.rangeEnd).split("T")[0]}T00:00:00`);
      const daily = [];

      for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
        const key = this.toLocalDateKey(cursor);
        daily.push(
          sourceMap.get(key) || {
            date: key,
            revenue: 0,
            orders: 0,
          }
        );
      }

      if (this.salesPeriod !== "year") return daily;

      const monthMap = new Map();
      daily.forEach((item) => {
        const monthKey = item.date.slice(0, 7);
        const current = monthMap.get(monthKey) || {
          date: `${monthKey}-01`,
          revenue: 0,
          orders: 0,
          label: `${Number(monthKey.slice(5, 7))}月`,
        };
        current.revenue += Number(item.revenue || 0);
        current.orders += Number(item.orders || 0);
        monthMap.set(monthKey, current);
      });
      return [...monthMap.values()];
    },

    toLocalDateKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatBarLabel(item) {
      if (item.label) return item.label;
      const date = new Date(`${item.date}T00:00:00`);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    },

    formatCurrency(value) {
      return new Intl.NumberFormat("zh-TW", {
        style: "currency",
        currency: "TWD",
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },

    formatNumber(value) {
      return new Intl.NumberFormat("zh-TW").format(Number(value || 0));
    },

    formatDateTime(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("zh-TW", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    statusClass(status) {
      const map = {
        已完成: "completed",
        已取消: "cancelled",
        已退款: "refunded",
        待確認: "pending",
        已接單: "processing",
        準備中: "processing",
        製作中: "processing",
        待取餐: "ready",
        待外送: "ready",
      };
      return map[String(status || "").trim()] || "unknown";
    },

    hotProductWidth(quantity) {
      if (this.maxHotProductQuantity <= 0) return "0%";
      return `${Math.max(
        5,
        Math.round((Number(quantity || 0) / this.maxHotProductQuantity) * 100)
      )}%`;
    },
  },

  mounted() {
    this.loadDashboard();
  },
};
